import React, { useEffect, useState } from 'react';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';
import Picture from './Picture';
import PictureStack from './PictureStack';
import AnalysisOutput from './picture-ai/AnalysisOutput';
import { API_BASE_URL } from '../config';
import ImageDetailsEditor from './ImageDetailsEditor';
import { getAdminBadge } from '../utils/adminBadge';
import { getFilterTranslations } from '../i18n/filters';
import { can } from '../utils/can.js';

/**
 * Where each value comes from, said next to the value itself.
 *
 * A shop name and a declared item look alike in a flat list, yet one is master data nobody on the
 * field can change and the other is what the contributor typed. Grouping by provenance is what
 * makes a record readable rather than a wall of pairs.
 */
const METADATA_SECTIONS = [
  { provenance: 'Store master data', fields: ['shopname', 'shop_code', 'shopsign', 'shopcity', 'shopzipcode', 'sector'] },
  { provenance: 'Field visit', fields: ['username', 'createdat', 'visittype', 'source'] },
  { provenance: 'Program configuration', fields: ['client', 'projectname', 'campaign'] },
  { provenance: 'Field form', fields: ['translatedquestion', 'item'] },
];

const ADMIN_ONLY_FIELDS = ['username'];

// On these sources a callid is one picture, not one visit: there is no visit to show.
const SOURCES_WITHOUT_VISITS = ['GA', 'TR'];

const hasVisit = (image) => !!image.callid && !image.deletedat && !SOURCES_WITHOUT_VISITS.includes(image.source);
const BADGED_FIELDS = ['client', 'username'];

const buildMetadata = (image, { translations, t, isAdmin, multiclient }) => METADATA_SECTIONS
  .map(section => ({
    provenance: t(section.provenance),
    rows: section.fields
      .filter(field => (isAdmin || !ADMIN_ONLY_FIELDS.includes(field)) && (field !== 'client' || multiclient))
      .map(field => ({
        field,
        adminOnly: BADGED_FIELDS.includes(field),
        label: field === 'projectname' ? translations.project
          : field === 'createdat' ? t('Date')
            : translations[field] || field,
        value: field === 'createdat'
          ? (image.createdat ? dayjs(image.createdat).format('L') : null)
          : image[field]
      }))
      .filter(row => row.value)
  }))
  .filter(section => section.rows.length);

const MetadataSection = ({ provenance, rows }) => (
  <div className="border-b border-gray-100 py-4 last:border-b-0 dark:border-gray-800">
    <p className="mb-2 text-[0.65rem] font-semibold uppercase tracking-[0.14em] text-gray-400 dark:text-gray-500">
      {provenance}
    </p>
    <dl className="space-y-2">
      {rows.map(row => (
        <div key={row.field} className="flex items-baseline justify-between gap-4">
          <dt className="flex shrink-0 items-center text-sm text-gray-500 dark:text-gray-400">
            {row.label}
            {row.adminOnly && getAdminBadge()}
          </dt>
          <dd className="min-w-0 break-words text-right text-sm font-medium text-gray-900 dark:text-white">{row.value}</dd>
        </div>
      ))}
    </dl>
  </div>
);

const TabButton = ({ active, onClick, children, count }) => (
  <button
    type="button"
    onClick={onClick}
    className={[
      'relative flex items-center gap-2 border-b-2 px-1 pb-2.5 text-sm font-medium transition',
      active
        ? 'border-blue-600 text-blue-700 dark:border-blue-400 dark:text-blue-300'
        : 'border-transparent text-gray-500 hover:text-gray-800 dark:text-gray-400 dark:hover:text-gray-200'
    ].join(' ')}
  >
    {children}
    {count > 0 && (
      <span className={[
        'rounded-full px-1.5 py-0.5 text-[0.65rem] font-semibold',
        active ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/60 dark:text-blue-200' : 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-300'
      ].join(' ')}>
        {count}
      </span>
    )}
  </button>
);

const PanelButton = ({ children, title, onClick, disabled, active, tone = 'neutral' }) => (
  <button
    type="button"
    title={title}
    onClick={onClick}
    disabled={disabled}
    className={[
      'inline-flex h-9 w-9 items-center justify-center rounded-lg border transition disabled:cursor-not-allowed disabled:opacity-50',
      active || tone === 'active'
        ? 'border-blue-200 bg-blue-50 text-blue-700 dark:border-blue-800 dark:bg-blue-950/50 dark:text-blue-300'
        : 'border-gray-200 bg-white text-gray-600 hover:bg-gray-50 dark:border-gray-700 dark:bg-gray-900 dark:text-gray-300 dark:hover:bg-gray-800'
    ].join(' ')}
  >
    {children}
  </button>
);

const MenuItem = ({ children, onClick, disabled, danger }) => (
  <button
    type="button"
    onClick={onClick}
    disabled={disabled}
    className={[
      'flex w-full items-center gap-2.5 px-3 py-2 text-left text-sm transition disabled:opacity-50',
      danger
        ? 'text-red-600 hover:bg-red-50 dark:text-red-400 dark:hover:bg-red-950/40'
        : 'text-gray-700 hover:bg-gray-50 dark:text-gray-200 dark:hover:bg-gray-800'
    ].join(' ')}
  >
    {children}
  </button>
);

const DeleteConfirmationModal = ({ onConfirm, onCancel, isDeleting }) => {
  const { t } = useTranslation();

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/75 p-4" onClick={onCancel}>
      <div className="w-full max-w-sm rounded-2xl bg-white p-6 shadow-2xl dark:bg-gray-900" onClick={e => e.stopPropagation()}>
        <h3 className="mb-3 flex items-center gap-2 text-lg font-semibold text-gray-900 dark:text-white">
          {getAdminBadge()}
          {t('Confirm deletion')}
        </h3>
        <p className="mb-5 text-sm text-gray-500 dark:text-gray-400">
          {t('Are you sure you want to delete this image? It will be kept in the database but will no longer be visible in Gallery.')}
        </p>
        <div className="flex justify-end gap-3">
          <button
            type="button"
            className="rounded-xl bg-gray-100 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-200 disabled:opacity-50 dark:bg-gray-800 dark:text-gray-200 dark:hover:bg-gray-700"
            onClick={onCancel}
            disabled={isDeleting}
          >
            {t('Cancel')}
          </button>
          <button
            type="button"
            className="rounded-xl bg-red-600 px-4 py-2 text-sm font-medium text-white hover:bg-red-700 disabled:bg-red-400"
            onClick={onConfirm}
            disabled={isDeleting}
          >
            {isDeleting ? t('Deleting...') : t('Delete')}
          </button>
        </div>
      </div>
    </div>
  );
};

function ImageModal({
  image,
  onClose,
  onToggleFavorite,
  onToggleAdminOnly,
  onDelete,
  onPrevious,
  onNext,
  hasPrevious,
  hasNext,
  curentUser,
  fetchWithAuth,
  onImageUpdate,
  onAddToExportCart,
  isInExportCart,
  onOpenVisit,
  visitPosition = null,
  onBackToVisit,
}) {
  const { t } = useTranslation();
  const [isDeleting, setIsDeleting] = useState(false);
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const [isTogglingAdminOnly, setIsTogglingAdminOnly] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [isRotating, setIsRotating] = useState(false);
  const [copyStatus, setCopyStatus] = useState(null);
  const [isDownloading, setIsDownloading] = useState(false);
  const [aiAnalyses, setAiAnalyses] = useState([]);
  const [activeTab, setActiveTab] = useState('info');
  const [menuOpen, setMenuOpen] = useState(false);
  const [aiLoading, setAiLoading] = useState(false);
  const [attachments, setAttachments] = useState([]);
  const [activeUrl, setActiveUrl] = useState(null);
  const isAdmin = !!curentUser?.isadmin;
  const canExport = can(curentUser, 'pictures.export');
  const canReadAi = can(curentUser, 'ai.read');

  useEffect(() => { setMenuOpen(false); }, [image.id]);

  useEffect(() => {
    if (!canReadAi) {
      setAiAnalyses([]);
      setAiLoading(false);
      return undefined;
    }
    let cancelled = false;
    setAiLoading(true);
    fetchWithAuth(`${API_BASE_URL}/api/picture-ai/pictures/${image.id}/analyses`)
      .then(async response => response.ok ? response.json() : [])
      .then(rows => { if (!cancelled) setAiAnalyses(rows); })
      .catch(() => { if (!cancelled) setAiAnalyses([]); })
      .finally(() => { if (!cancelled) setAiLoading(false); });
    return () => { cancelled = true; };
  }, [fetchWithAuth, image.id, canReadAi]);

  useEffect(() => {
    setActiveUrl(null);
    if (!image.has_attachments) {
      setAttachments([]);
      return undefined;
    }
    let cancelled = false;
    fetchWithAuth(`${API_BASE_URL}/api/pictures/${image.id}/attachments`)
      .then(async response => response.ok ? response.json() : { attachments: [] })
      .then(body => { if (!cancelled) setAttachments(body.attachments ?? []); })
      .catch(() => { if (!cancelled) setAttachments([]); });
    return () => { cancelled = true; };
  }, [fetchWithAuth, image.id, image.has_attachments]);

  useEffect(() => {
    const handleKeyPress = (e) => {
      if (isEditing) return;
      if (e.key === 'ArrowLeft' && hasPrevious) onPrevious();
      if (e.key === 'ArrowRight' && hasNext) onNext();
      if (e.key === 'Escape') {
        if (showDeleteConfirmation) setShowDeleteConfirmation(false);
        else if (menuOpen) setMenuOpen(false);
        else onClose();
      }
    };

    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, [hasNext, hasPrevious, isEditing, menuOpen, onClose, onNext, onPrevious, showDeleteConfirmation]);

  useEffect(() => {
    if (!copyStatus) return undefined;

    const timeout = window.setTimeout(() => setCopyStatus(null), 2200);
    return () => window.clearTimeout(timeout);
  }, [copyStatus]);

  const handleDelete = async () => {
    setIsDeleting(true);
    try {
      await onDelete(image.id);
      onClose();
    } catch (error) {
      console.error('Failed to delete image:', error);
      alert('Failed to delete image');
    } finally {
      setIsDeleting(false);
      setShowDeleteConfirmation(false);
    }
  };

  const handleToggleAdminOnly = async () => {
    setIsTogglingAdminOnly(true);
    try {
      await onToggleAdminOnly(image.id, !image.admin_only);
    } catch (error) {
      console.error('Failed to toggle admin only:', error);
      alert(t('Error toggling admin only'));
    } finally {
      setIsTogglingAdminOnly(false);
    }
  };

  const handleRotate = async (angle) => {
    if (isRotating) return;

    setIsRotating(true);
    try {
      const response = await fetchWithAuth(`${API_BASE_URL}/api/pictures/${image.id}/rotate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ angle }),
      });

      if (!response.ok) {
        throw new Error('Rotation failed');
      }

      const data = await response.json();

      if (onImageUpdate) {
        const newUrl = data.url || image.pictureurl;
        const cleanUrl = newUrl.split('?')[0];

        onImageUpdate({
          ...image,
          pictureurl: `${cleanUrl}?t=${Date.now()}`
        });
      }
    } catch (error) {
      console.error('Failed to rotate image:', error);
      alert('Erreur lors de la rotation de l\'image');
    } finally {
      setIsRotating(false);
    }
  };

  const handleCopyShareLink = async (e) => {
    e.stopPropagation();

    const url = `${window.location.origin}/gallery/share/${image.id}`;

    try {
      await navigator.clipboard.writeText(url);
      setCopyStatus(t('Share link copied'));
    } catch (error) {
      console.error('Failed to copy share link:', error);
      setCopyStatus(t('Unable to copy share link'));
    }
  };

  const handleDownload = async (e) => {
    e.stopPropagation();

    if (isDownloading) return;

    setIsDownloading(true);
    try {
      const response = await fetchWithAuth(`${API_BASE_URL}/api/export-pictures`, {
        method: 'POST',
        body: JSON.stringify({
          pictureIds: [image.id],
          addMetadata: true,
          format: 'zip'
        })
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Export failed');
      }

      const contentType = response.headers.get('content-type');

      if (contentType && contentType.startsWith('image/')) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;

        const contentDisposition = response.headers.get('content-disposition');
        const filenameMatch = contentDisposition?.match(/filename="(.+)"/);
        link.download = filenameMatch ? filenameMatch[1] : `${image.shopname}_${image.createdat}.jpg`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      } else {
        const data = await response.json();
        const link = document.createElement('a');
        link.href = `${window.location.origin}/downloads/${data.downloadUrl}`;
        link.download = data.downloadUrl;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }
    } catch (error) {
      console.error('Download failed:', error);
      alert(t('Download failed') + ': ' + error.message);
    } finally {
      setIsDownloading(false);
    }
  };

  const translations = getFilterTranslations(t);
  const metadata = buildMetadata(image, {
    translations, t, isAdmin, multiclient: !!curentUser?.multiclient
  });
  const currentAnalyses = aiAnalyses.filter(analysis => analysis.is_current);

  const stageButton = 'inline-flex h-10 w-10 items-center justify-center rounded-full border border-white/15 bg-black/45 text-white shadow-lg backdrop-blur-md transition hover:bg-black/65 disabled:cursor-not-allowed disabled:opacity-50';

  return (
    <>
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-gray-950/90 p-3 backdrop-blur-sm" onClick={onClose}>
        <div
          className="relative flex h-[92vh] w-full max-w-7xl flex-col overflow-hidden rounded-3xl bg-white shadow-2xl dark:bg-gray-900 lg:flex-row"
          onClick={e => e.stopPropagation()}
        >
          {/* The photo keeps a stage of its own: nothing but navigation sits on top of it any more. */}
          <div className="relative flex h-[42vh] min-h-0 shrink-0 items-center justify-center bg-gray-950 lg:h-auto lg:flex-1">
            <Picture pictureurl={activeUrl ?? image.pictureurl} className="h-full max-h-full w-full object-contain" />

            {attachments.length > 0 && (
              <PictureStack
                mainUrl={image.pictureurl}
                attachments={attachments}
                activeUrl={activeUrl ?? image.pictureurl}
                onSelect={url => setActiveUrl(url === image.pictureurl ? null : url)}
              />
            )}

            {visitPosition && (
              <div className="absolute left-4 top-4 z-20 flex items-center gap-2 rounded-full border border-white/15 bg-black/55 py-1 pl-3 pr-1 text-sm text-white shadow-lg backdrop-blur-md">
                <span>{t('Photo {{index}} of {{total}} in the visit', visitPosition)}</span>
                <button type="button" onClick={onBackToVisit} className="rounded-full bg-white/15 px-3 py-1 font-medium transition hover:bg-white/25">
                  {t('Back to visit')}
                </button>
              </div>
            )}

            {hasPrevious && (
              <button type="button" title={t('Previous')} onClick={onPrevious} className={`${stageButton} absolute left-4 top-1/2 z-20 -translate-y-1/2`}>
                <svg className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
                </svg>
              </button>
            )}
            {hasNext && (
              <button type="button" title={t('Next')} onClick={onNext} className={`${stageButton} absolute right-4 top-1/2 z-20 -translate-y-1/2`}>
                <svg className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                </svg>
              </button>
            )}

            {isAdmin && !activeUrl && (
              <div className="absolute bottom-4 left-1/2 z-20 flex -translate-x-1/2 gap-2">
                <button type="button" title={t('Rotate 90° left')} onClick={() => handleRotate(-90)} disabled={isRotating} className={stageButton}>
                  <svg className={`h-5 w-5 ${isRotating ? 'animate-spin' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h10a8 8 0 018 8v2M3 10l6 6m-6-6l6-6" />
                  </svg>
                </button>
                <button type="button" title={t('Rotate 90° right')} onClick={() => handleRotate(90)} disabled={isRotating} className={stageButton}>
                  <svg className={`h-5 w-5 ${isRotating ? 'animate-spin' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 10h-10a8 8 0 00-8 8v2M21 10l-6 6m6-6l-6-6" />
                  </svg>
                </button>
              </div>
            )}
          </div>

          <aside className="flex min-h-0 w-full flex-1 flex-col border-t border-gray-200 dark:border-gray-800 lg:w-[26rem] lg:flex-none lg:border-l lg:border-t-0">
            <header className="flex items-start justify-between gap-3 px-5 pt-5">
              <div className="min-w-0">
                <p className="flex items-center gap-1 text-[0.65rem] font-semibold uppercase tracking-[0.16em] text-gray-400 dark:text-gray-500">
                  {t('Photo record')}
                  {isAdmin && (
                    <>
                      <span aria-hidden="true">·</span>
                      {getAdminBadge()}
                      <span className="tracking-normal">#{image.id}</span>
                    </>
                  )}
                </p>
                <h2 className="mt-1 truncate text-lg font-semibold text-gray-900 dark:text-white">{image.shopname}</h2>
                <p className="mt-0.5 text-sm text-gray-500 dark:text-gray-400">
                  {dayjs(image.createdat).format('L')}
                  {isAdmin && image.username ? ` · ${image.username}` : ''}
                </p>
                {onOpenVisit && !visitPosition && hasVisit(image) && (
                  <button
                    type="button"
                    onClick={() => onOpenVisit(image)}
                    className="mt-2 inline-flex items-center gap-1.5 text-sm font-medium text-blue-600 transition hover:text-blue-800 hover:underline dark:text-blue-400 dark:hover:text-blue-300"
                  >
                    <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M4 5h6v6H4zM14 5h6v6h-6zM4 15h6v4H4zM14 15h6v4h-6z" />
                    </svg>
                    {t('See in visit context')}
                  </button>
                )}
                {(image.deletedat || image.admin_only) && (
                  <div className="mt-2 flex flex-wrap gap-1.5">
                    {image.deletedat && (
                      <span className="rounded-full bg-red-100 px-2 py-0.5 text-xs font-medium text-red-700 dark:bg-red-950/60 dark:text-red-300">{t('Deleted')}</span>
                    )}
                    {image.admin_only && (
                      <span className="rounded-full bg-purple-100 px-2 py-0.5 text-xs font-medium text-purple-700 dark:bg-purple-950/60 dark:text-purple-300">{t('Not visible for the client')}</span>
                    )}
                  </div>
                )}
              </div>
              <button
                type="button"
                title={t('Close')}
                onClick={onClose}
                className="shrink-0 rounded-lg p-1.5 text-gray-400 transition hover:bg-gray-100 hover:text-gray-700 dark:hover:bg-gray-800 dark:hover:text-gray-200"
              >
                <svg className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </header>

            {canReadAi && (
              <nav className="mt-4 flex gap-5 border-b border-gray-200 px-5 dark:border-gray-800">
                <TabButton active={activeTab === 'info'} onClick={() => setActiveTab('info')}>{t('Information')}</TabButton>
                <TabButton active={activeTab === 'ai'} onClick={() => setActiveTab('ai')} count={currentAnalyses.length}>
                  <span className="text-blue-500 dark:text-blue-400">✦</span>
                  {t('AI analysis')}
                </TabButton>
              </nav>
            )}

            <div className="min-h-0 flex-1 overflow-y-auto px-5">
              {activeTab === 'info' || !canReadAi ? (
                isEditing ? (
                  <div className="py-4">
                    <ImageDetailsEditor
                      image={image}
                      fetchWithAuth={fetchWithAuth}
                      onCancel={() => setIsEditing(false)}
                      onSaved={(updatedImage) => {
                        setIsEditing(false);
                        if (onImageUpdate) onImageUpdate(updatedImage);
                      }}
                    />
                  </div>
                ) : (
                  metadata.map(section => <MetadataSection key={section.provenance} {...section} />)
                )
              ) : (
                <div className="space-y-3 py-4">
                  {aiLoading && <p className="text-sm text-gray-500 dark:text-gray-400">{t('Loading...')}</p>}
                  {!aiLoading && aiAnalyses.length === 0 && (
                    <div className="rounded-xl border border-dashed border-gray-300 p-6 text-center dark:border-gray-700">
                      <p className="text-2xl text-gray-300 dark:text-gray-600">✦</p>
                      <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">{t('This picture has not been analyzed yet.')}</p>
                    </div>
                  )}
                  {aiAnalyses.map(analysis => (
                    <article key={analysis.id} className="rounded-xl border border-gray-200 dark:border-gray-800">
                      <div className="flex items-center justify-between gap-2 border-b border-gray-100 px-3 py-2 dark:border-gray-800">
                        <span className="min-w-0 truncate text-sm font-medium text-gray-900 dark:text-white">{analysis.configuration_name}</span>
                        <span className={[
                          'shrink-0 rounded-full px-2 py-0.5 text-[0.65rem] font-semibold',
                          analysis.status === 'succeeded'
                            ? 'bg-green-100 text-green-700 dark:bg-green-950/60 dark:text-green-300'
                            : analysis.status === 'failed'
                              ? 'bg-red-100 text-red-700 dark:bg-red-950/60 dark:text-red-300'
                              : 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-300'
                        ].join(' ')}>
                          {analysis.status}
                        </span>
                      </div>
                      <div className="p-3">
                        {analysis.normalized_output ? (
                          <>
                            {/* The agent's own view: a compliance verdict and a list of extracted
                                products share nothing but the envelope. Criterion labels come from
                                the version that produced this analysis, not the active one. */}
                            <AnalysisOutput output={analysis.normalized_output} configParams={analysis.config_params} />
                            <details className="mt-3">
                              <summary className="cursor-pointer text-xs text-gray-500 dark:text-gray-400">{t('Structured details')}</summary>
                              <pre className="mt-2 overflow-x-auto whitespace-pre-wrap rounded-lg bg-gray-950 p-3 text-xs text-gray-100">{JSON.stringify(analysis.normalized_output.details, null, 2)}</pre>
                            </details>
                            {(analysis.raw_output || analysis.error || analysis.logs?.length) && (
                              <details className="mt-2">
                                <summary className="cursor-pointer text-xs text-gray-500 dark:text-gray-400">{t('Raw output and diagnostics')}</summary>
                                <pre className="mt-2 overflow-x-auto whitespace-pre-wrap rounded-lg bg-gray-950 p-3 text-xs text-gray-100">{analysis.raw_output || ''}{analysis.error ? `\n\n${JSON.stringify(analysis.error, null, 2)}` : ''}{analysis.logs?.length ? `\n\n${JSON.stringify(analysis.logs, null, 2)}` : ''}</pre>
                              </details>
                            )}
                          </>
                        ) : (
                          <p className="text-sm text-gray-500 dark:text-gray-400">
                            {analysis.status === 'failed' ? t('The analysis failed.') : t('Analysis pending or running.')}
                          </p>
                        )}
                      </div>
                    </article>
                  ))}
                </div>
              )}
            </div>

            <footer className="flex items-center gap-2 border-t border-gray-200 px-5 py-3 dark:border-gray-800">
              {onToggleFavorite && (
                <PanelButton
                  title={image.favorite ? t('Remove from favorites') : t('Add to favorites')}
                  onClick={() => onToggleFavorite(image.id)}
                  active={image.favorite}
                >
                  <svg className={`h-4 w-4 ${image.favorite ? 'fill-current text-red-500' : ''}`} viewBox="0 0 24 24" stroke="currentColor" strokeWidth={image.favorite ? '0' : '2'} fill="none">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
                  </svg>
                </PanelButton>
              )}

              <PanelButton title={t('Copy share link')} onClick={handleCopyShareLink}>
                <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M9.75 14.25l4.5-4.5" />
                  <path strokeLinecap="round" strokeLinejoin="round" d="M12.75 7.25l1.05-1.05a3.25 3.25 0 014.6 4.6l-2.1 2.1a3.25 3.25 0 01-4.6 0" />
                  <path strokeLinecap="round" strokeLinejoin="round" d="M11.25 16.75l-1.05 1.05a3.25 3.25 0 01-4.6-4.6l2.1-2.1a3.25 3.25 0 014.6 0" />
                </svg>
              </PanelButton>

              {canExport && (
                <>
                  <PanelButton
                    title={isInExportCart ? t('Already in export cart') : t('Add to export cart')}
                    onClick={() => onAddToExportCart?.(image)}
                    active={isInExportCart}
                  >
                    {isInExportCart ? (
                      <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                      </svg>
                    ) : (
                      <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M3 3h2l.4 2M7 13h10l3-8H5.4M7 13L5.4 5M7 13l-2 6h14" />
                      </svg>
                    )}
                  </PanelButton>
                  <PanelButton title={t('Download image')} onClick={handleDownload} disabled={isDownloading}>
                    <svg className={`h-4 w-4 ${isDownloading ? 'animate-pulse' : ''}`} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                  </PanelButton>
                </>
              )}

              {copyStatus && <span className="truncate text-xs text-gray-500 dark:text-gray-400">{copyStatus}</span>}

              {/* Administration lives behind one control: these actions are rare and irreversible,
                  and lining them up with the everyday ones gave all nine the same weight. */}
              {isAdmin && (
                <div className="relative ml-auto">
                  <PanelButton title={t('More actions')} onClick={() => setMenuOpen(open => !open)} active={menuOpen}>
                    <svg className="h-4 w-4" viewBox="0 0 24 24" fill="currentColor">
                      <circle cx="5" cy="12" r="1.6" /><circle cx="12" cy="12" r="1.6" /><circle cx="19" cy="12" r="1.6" />
                    </svg>
                  </PanelButton>
                  {menuOpen && (
                    <>
                      <div className="fixed inset-0 z-10" onClick={() => setMenuOpen(false)} />
                      <div className="absolute bottom-11 right-0 z-20 w-56 overflow-hidden rounded-xl border border-gray-200 bg-white py-1 shadow-xl dark:border-gray-700 dark:bg-gray-900">
                        <MenuItem onClick={() => { setMenuOpen(false); setActiveTab('info'); setIsEditing(true); }}>
                          <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                          </svg>
                          {t('Edit information')}
                        </MenuItem>
                        <MenuItem onClick={() => { setMenuOpen(false); handleToggleAdminOnly(); }} disabled={isTogglingAdminOnly}>
                          <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={image.admin_only ? 'M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z' : 'M8 11V7a4 4 0 118 0m-4 8v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2z'} />
                          </svg>
                          {image.admin_only ? t('Make photo visible to all') : t('Restrict photo to admins')}
                        </MenuItem>
                        {!image.deletedat && (
                          <MenuItem danger onClick={() => { setMenuOpen(false); setShowDeleteConfirmation(true); }}>
                            <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                            </svg>
                            {t('Delete')}
                          </MenuItem>
                        )}
                      </div>
                    </>
                  )}
                </div>
              )}
            </footer>
          </aside>
        </div>
      </div>

      {showDeleteConfirmation && (
        <DeleteConfirmationModal
          onConfirm={handleDelete}
          onCancel={() => setShowDeleteConfirmation(false)}
          isDeleting={isDeleting}
        />
      )}
    </>
  );
}

export default ImageModal;
